import React from 'react';

import { useDictionary } from '@/src/app/hooks';
import { MyLessons_SelectedIcon, MyLessons_UnselectedIcon, type UnitDTO } from '@/src/ui';

interface LessonListProps {
    unit: UnitDTO;
    grade: string;
    currentUnit: number;
    myLessons: string[];
    setSelectedLessonIndex: (lesson: number | null) => void;
    addMyLesson: (lessonId: string) => void;
    removeMyLesson: (lessonId: string) => void;
}

const LessonList: React.FC<LessonListProps> = ({
    unit,
    grade,
    currentUnit,
    myLessons,
    setSelectedLessonIndex,
    addMyLesson,
    removeMyLesson,
}) => {
    const { translate } = useDictionary();
    if (!unit.lessons || unit.lessons.length === 0) {
        return null;
    }
    const displayLessons = unit.lessons
        .map((lesson, lessonIndex) => ({ lesson, lessonIndex }))
        .filter(({ lesson }) => lesson.lessonNumber !== 99);
    const splitIndex = Math.ceil(displayLessons.length / 2);

    const column1Children: React.JSX.Element[] = [];
    const column2Children: React.JSX.Element[] = [];

    displayLessons.forEach(({ lesson, lessonIndex }, displayIndex) => {
        const unitIndex = currentUnit;

        const lessonId = `G${grade}_U${unitIndex}_L${lessonIndex}`;
        const isSaved = myLessons && myLessons?.includes(lessonId);
        const SvgIcon = isSaved ? MyLessons_SelectedIcon : MyLessons_UnselectedIcon;
        const onClick = isSaved ? () => removeMyLesson(lessonId) : () => addMyLesson(lessonId);

        const lessonDiv = (
            <div className="lesson">
                <a
                    href="#"
                    className={`lesson-link pd-G${grade}-lesson-link`}
                    onClick={(e) => {
                        e.preventDefault();
                        setSelectedLessonIndex(lessonIndex);
                    }}
                >
                    <span className="lesson-number-line">
                        {translate('Lesson')} {lesson.lessonNumber}:
                        {lesson.lessonType && (
                            <span className={`lesson-type-label pd-G${grade}-primary background`}>
                                {translate(lesson.lessonType)}
                            </span>
                        )}
                    </span>
                    {lesson.title}
                </a>
                <a
                    id={`G${grade}lesson${lessonIndex}myLessonsButton`}
                    className="myLessonsButton"
                    data-lesson-id={`G${grade}_U${currentUnit}_L${lessonIndex}`}
                    onClick={onClick}
                >
                    <SvgIcon color={`g${grade}`} />
                </a>
            </div>
        );

        if (displayIndex < splitIndex) {
            column1Children.push(lessonDiv);
        } else {
            column2Children.push(lessonDiv);
        }
    });

    let index = 0;
    return (
        <>
            <div className="lesson-columns row">
                <div className="lesson-column col-12 col-md-6 lesson-col-1">
                    {column1Children.map((c) => (
                        <React.Fragment key={`lesson-col-1-${index++}`}>{c}</React.Fragment>
                    ))}
                </div>
                <div className="lesson-column col-12 col-md-6 lesson-col-2">
                    {column2Children.map((c) => (
                        <React.Fragment key={`lesson-col-2-${index++}`}>{c}</React.Fragment>
                    ))}
                </div>
            </div>
        </>
    );
};

export default LessonList;
